import React, { useEffect, useMemo } from 'react';
import { Typography } from 'antd';
import TwoColumnSection from 'components/two-column-section/TwoColumnSection';
import { Text } from 'components/text/Text';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import { OfferSearchResponseDto } from 'logic/api-models/api-models';
import { Stack } from 'components/stack/stack';
import {
  ContentContainerWrapper,
  ContentWrapper,
  LeftColumn,
  RightColumn,
} from '../components/common';
import loadable from '@loadable/component';
import { getRegularOfferCardTitle } from '../components/common.logic';
import { colors } from 'styles/colors';
import { Heading } from 'components/heading/Heading';
import { SearchResultCityContentSection } from '../components/search-result-city-content-section/search-result-city-content-section';
import { PixelEvents, triggerFBPixelEvent } from 'logic/analytics/analytics';
import { InfoBox } from '../../user/user-profile/components/InfoBox';
import { AppRoute } from '../../../app/app.route.const';
import { navigationSlice } from '../../../logic/store/navigation/navigation.slice';
import { useDispatch } from 'react-redux';
import { AlternativeConsulting } from '../../home/components/AlternativeConsulting';
import { SortingField } from '../components/Sorting';
import { Link } from 'gatsby';
import { renderTagsIntoText } from 'logic/utils/render';
import { generatePath } from 'react-router-dom';
import { useValidSubjectOrNotFound } from 'src/pages-all/search/hooks/useValidSubjectOrNotFound';
import {
  isCityDistrictOfBerlin,
  isCityExactlyBerlin,
} from 'logic/store/offer/search-offer/search-offer.logic';
import { NearbyPlaces } from 'src/pages-all/search/components/NearbyPlaces';
import {
  BERLIN_CITY_DISTRICT_SLUGS,
  getNearbyPlacesForBerlin,
} from 'logic/berlin/berlin-city-districts';

const SearchItemList = loadable(() => import('../components/SearchItemList'));
const { Title, Paragraph } = Typography;

interface SearchResultsLocalTutorsPageProps {
  city: string | undefined;
  citySlug: string | undefined;
  subject: string | undefined;
  subjectSlug: string | undefined;
  offers: OfferSearchResponseDto | undefined;
}

export const SearchResultsLocalTutorsPage: React.FC<SearchResultsLocalTutorsPageProps> = ({
  city,
  citySlug,
  subject,
  subjectSlug,
  offers,
}) => {
  useValidSubjectOrNotFound();

  const { minWideDesktop } = useBreakpoints();
  const hasResults = offers && offers.results && offers.results.length > 0;
  const dispatch = useDispatch();

  useEffect(() => {
    triggerFBPixelEvent(PixelEvents.ViewContent);
  }, []);

  const nearbyPlaces = useMemo(() => {
    if (
      offers?.nearbyPlaces &&
      offers.nearbyPlaces.length > 0 &&
      city &&
      !isCityDistrictOfBerlin(city)
    ) {
      return offers.nearbyPlaces;
    }
  }, [city, offers]);
  const nearbyPlacesBerlinDistricts = useMemo(() => {
    if (city && (isCityDistrictOfBerlin(city) || isCityExactlyBerlin(city))) {
      return getNearbyPlacesForBerlin(city);
    }
  }, [city]);

  if (!offers) {
    return null;
  }

  return (
    <ContentContainerWrapper background="teal">
      <ContentWrapper gap={0.5} flow="row">
        <div>
          <Title>
            {city && subject && subject !== ''
              ? translate(translationKeys.searchResultsPage.localTutorsPerCity.title, {
                  count: offers?.totalCount || 0,
                  subject,
                  city,
                })
              : city
              ? translate(translationKeys.searchResultsPage.localTutorsPerCity.titleNoSubject, {
                  count: offers?.totalCount || 0,
                  subject,
                  city,
                })
              : subject
              ? translate(translationKeys.searchResultsPage.localTutors.title, {
                  count: offers?.totalCount || 0,
                  subject,
                  city,
                })
              : translate(translationKeys.searchResultsPage.common.titleNoInformation, {
                  count: offers?.totalCount || 0,
                  subject,
                  city,
                })}
          </Title>
          <Paragraph>
            <Text type="secondary">
              <Link to="/">{translate(translationKeys.common.pagination.start)}</Link> {'>'}{' '}
              {translate(translationKeys.common.pagination.search.localTutors)}{' '}
              {/* SEO Improvement Test  */}
              {city && isCityDistrictOfBerlin(citySlug) && (
                <>
                  {' '}
                  {'>'}{' '}
                  <Link
                    to={generatePath(AppRoute.SearchLocalTutorsPerCity, {
                      city: 'berlin',
                    })}
                  >
                    Berlin
                  </Link>{' '}
                </>
              )}
              {city && (
                <>
                  {'> '}
                  <Link
                    to={generatePath(AppRoute.SearchLocalTutorsPerCity, {
                      city: citySlug,
                    })}
                  >
                    {city}
                  </Link>{' '}
                </>
              )}
              {subject && `> ${subject} `}
            </Text>
          </Paragraph>
        </div>
        {hasResults && (
          <div>
            <Paragraph>
              <Text>
                {city &&
                  translate(translationKeys.searchResultsPage.localTutorsPerCity.paragraph1, {
                    count: offers?.totalCount || 0,
                    city,
                    subject,
                  })}
                {!city &&
                  translate(translationKeys.searchResultsPage.localTutors.paragraph1, {
                    count: offers?.totalCount || 0,
                    city,
                    subject,
                  })}
                {subjectSlug && ' '}
                {subjectSlug &&
                  renderTagsIntoText(
                    translate(translationKeys.searchResultsPage.localTutors.paragraph1Extra, {
                      count: offers?.totalCount || 0,
                      city,
                      subject,
                    }),
                    (key, content) => (
                      <a
                        href={`/${encodeURIComponent(key)}/nachhilfe/${encodeURIComponent(
                          subjectSlug
                        )}`}
                      >
                        {content}
                      </a>
                    )
                  )}
              </Text>
            </Paragraph>
            <Paragraph>
              <Text>
                {city &&
                  renderTagsIntoText(
                    translate(translationKeys.searchResultsPage.localTutorsPerCity.paragraph2, {
                      count: offers?.totalCount || 0,
                      city,
                      subject,
                    }),
                    (key, content) => (
                      <a href={`/nachhilfe/${encodeURIComponent(key)}`}>{content}</a>
                    )
                  )}
                {!city &&
                  translate(translationKeys.searchResultsPage.localTutors.paragraph2, {
                    count: offers?.totalCount || 0,
                    city,
                    subject,
                  })}
              </Text>
            </Paragraph>
            <SortingField />
          </div>
        )}
      </ContentWrapper>
      {hasResults && (
        <Heading level={2} color={colors.blueGray}>
          {translate(
            subject && city
              ? translationKeys.searchResultsPage.common.subtitleLocalSubjectCity
              : subject
              ? translationKeys.searchResultsPage.common.subtitleLocalSubject
              : translationKeys.searchResultsPage.common.subtitleLocalCity,
            { subject, city }
          )}
        </Heading>
      )}
      {!hasResults && !isCityDistrictOfBerlin(city || '') && (
        <Heading align="center" level={4} color={colors.blueGray}>
          {translate(translationKeys.searchResultsPage.common.recommendedEliteTutor, { subject })}
        </Heading>
      )}
      {!hasResults && isCityDistrictOfBerlin(city || '') && (
        <div>
          <Heading align="center" level={4} color={colors.blueGray}>
            {translate(translationKeys.searchResultsPage.common.noResultsFoundBerlin, { subject })}
          </Heading>
          <ul>
            {BERLIN_CITY_DISTRICT_SLUGS.map((suggestion, i) => (
              <li key={i}>
                <a href={`/${suggestion}/nachhilfe`}>
                  {suggestion
                    .split('-')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join('-')}
                </a>{' '}
              </li>
            ))}
          </ul>
        </div>
      )}
      {!minWideDesktop ? (
        <LeftColumn>
          <SearchItemList offers={offers} getOfferTitle={getRegularOfferCardTitle} />
        </LeftColumn>
      ) : (
        <TwoColumnSection
          left={
            <LeftColumn>
              <SearchItemList offers={offers} getOfferTitle={getRegularOfferCardTitle} />
            </LeftColumn>
          }
          right={
            <RightColumn>
              <Stack gap={0.5} flow="row" alignItems="start">
                <InfoBox
                  heading={translate(translationKeys.stickySideBar.promotion_2.headline)}
                  paragraphsTranslationKeys={translationKeys.stickySideBar.promotion_2.paragraphs}
                  buttonText={translate(translationKeys.stickySideBar.promotion_2.buttonText)}
                  buttonAction={() =>
                    dispatch(
                      navigationSlice.actions.navigateTo({
                        path: AppRoute.External_TrialHour_Search_Right,
                      })
                    )
                  }
                  isSticky
                  topOffset={100}
                  bottomOffset={0}
                />
              </Stack>
            </RightColumn>
          }
        />
      )}
      <AlternativeConsulting />
      <SearchResultCityContentSection />
      {nearbyPlaces && <NearbyPlaces city={city} places={nearbyPlaces} />}
      {nearbyPlacesBerlinDistricts && (
        <NearbyPlaces
          city={city}
          places={nearbyPlacesBerlinDistricts}
          title={translate(translationKeys.searchResultsPage.common.nearbyCityDistricts)}
          fallbackImage="berlin"
        />
      )}
    </ContentContainerWrapper>
  );
};
